import { merge, isEmpty } from 'lodash'
import { getBrandName } from '../brand'
import { getYear } from 'date-fns'
import { PartnerType } from '../common/enum'

/**
 * This service is a bridge between the application and whatever analytics
 * software is used.
 *
 * By defining the REACT_APP_ANALYTICS environment variable we are able to
 * either export the real or mocked implementation, so that from the application
 * point of view nothing changes and it can continue calling the analytics logic
 * as per usually.
 */

window.adobeDataLayer = window.adobeDataLayer || []

export const getBrowserResolutionBreakpointString = width => {
  if (width <= 480) return 'xs'
  // eslint-disable-next-line no-else-return
  else if (width > 480 && width <= 720) return 's'
  // eslint-disable-next-line no-else-return
  else if (width > 720 && width <= 960) return 'm'
  // eslint-disable-next-line no-else-return
  else if (width > 960 && width <= 1280) return 'l'
  // eslint-disable-next-line no-else-return
  else if (width > 1280 && width <= 1600) return 'xl'
  // eslint-disable-next-line no-else-return
  else return 'xxl'
}

const checkAndRemoveEmptyProps = obj => {
  // eslint-disable-next-line no-shadow
  const areEmptyProps = obj => {
    const newObj = Array.isArray(obj) ? [] : {}
    Object.keys(obj).forEach(key => {
      if (obj[key] === Object(obj[key]) && !isEmpty(obj[key])) {
        // @ts-ignore
        newObj[key] = areEmptyProps(obj[key])
      }
      // @ts-ignore
      else if (
        typeof obj[key] !== 'number' &&
        (obj[key] === undefined || obj[key] === null || Object.keys(obj[key]).length === 0)
      ) {
        // @ts-ignore
        result = true
      }
    })
    return newObj
  }
  // eslint-disable-next-line no-shadow
  const removeEmptyProps = obj => {
    const newObj = Array.isArray(obj) ? [] : {}
    Object.keys(obj).forEach(key => {
      if (obj[key] === Object(obj[key]) && !isEmpty(obj[key])) {
        // @ts-ignore
        newObj[key] = removeEmptyProps(obj[key])
      }
      // @ts-ignore
      else if (
        obj[key] !== undefined &&
        obj[key] !== null &&
        (Object.keys(obj[key]).length !== 0 || typeof obj[key] === 'number')
      ) {
        // @ts-ignore
        newObj[key] = obj[key]
      }
    })
    return newObj
  }
  let result = true
  let newObj = obj
  while (result) {
    result = false
    newObj = removeEmptyProps(newObj)
    areEmptyProps(newObj)
  }
  return newObj
}

const mock = {
  default: {},
  isLive: false,
  productStorageKey: 'analyticsProduct',
  productSet: false,
  setDefaults: function setDefaults(obj) {
    this.default = merge(this.default, obj)
  },
  loadStoredProduct: function loadStoredProduct() {
    const storedProductJSON = sessionStorage.getItem(this.productStorageKey)
    if (storedProductJSON) {
      this.setDefaults({ product: JSON.parse(storedProductJSON) })
    }
  },

  push: function push(obj) {
    let newEntry = merge(
      {
        design: {
          browserResolutionBreakpoint: getBrowserResolutionBreakpointString(window.innerWidth),
        },
      },
      this.default,
      obj
    )
    if (obj.product) {
      this.setDefaults({ product: obj.product })
      // Keep the analytics product stored, after payment page gets refreshed and the data is lost
      sessionStorage.setItem(this.productStorageKey, JSON.stringify(obj.product))
    }
    newEntry = {
      ...{
        // do not remove null values from form or errors to be able to "reset" the previously set error and forms
        form: this.default.form,
        error: this.default.error,
      },
      ...newEntry,
    }
    window.adobeDataLayer.push(newEntry)
  },

  getProductFromFormData: formData => {
    if (['dev', 'int'].includes(process.env.REACT_APP_ENV)) {
      // eslint-disable-next-line no-console
      console.debug('getProductFromFormData', formData)
    }

    const {
      productType,
      paymentType,
      numberMonthlyInstallments,
      contractDuration,
      contractAnnualMileage,
      contractGrossPriceOneTime,
      salesChannelType,
      carModelDescription,
      carRegistrationDate,
      customerType,
      contractGrossPriceMonthly,
      carNew,
    } = formData
    let condition
    if (carNew === undefined) {
      condition = null
    } else {
      condition = carNew ? 'new' : 'used'
    }
    return [
      {
        productVariants: [productType.value],
        attributes: {
          typeOfSale: salesChannelType ?? null,
          recurringPayment: contractGrossPriceMonthly ?? null,
          paymentFrequency: paymentType === 'ONE_OFF' ? 'single' : 'monthly',
          duration: paymentType === 'ONE_OFF' ? null : numberMonthlyInstallments ?? contractDuration,
          durationUnit: paymentType === 'ONE_OFF' ? null : 'MONTH',
          contractAmount:
            contractGrossPriceOneTime ??
            contractGrossPriceMonthly * (numberMonthlyInstallments ?? contractDuration ?? 12) ??
            null,
        },
        vehicleModel: [
          {
            manufacturer: getBrandName() === 'DEALER' ? null : getBrandName(),
            name: carModelDescription ?? null,
            initialRegistrationDate: carRegistrationDate ?? null,
            typeOfUse: customerType ?? null,
            condition,
          },
        ],
      },
    ]
  },

  getCustomerFromFormData: formData => {
    const { customerType, customerBirthDate, customerPostCode } = formData
    const yearOfBirth = getYear(customerBirthDate)
    return {
      yearOfBirth: Number.isNaN(yearOfBirth) ? null : yearOfBirth,
      loggedInUserGroup: customerType ? (customerType === PartnerType.BUSINESS ? 'business' : 'private') : null,
      address: {
        zipCode: customerPostCode,
      },
    }
  },
}

const real = {
  ...mock,
  isLive: true,
}

let implementation = mock // eslint-disable-line

if (process.env.REACT_APP_ANALYTICS) {
  implementation = real
}

export { implementation as Analytics }
